import { ApolloClient, InMemoryCache, HttpLink, from, split } from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { logErrorMessages } from "@vue/apollo-util";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient as createWsClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";

declare module "@apollo/client/core" {
  export interface DefaultContext {
    token?: string;
    hasAccessAllProjects?: boolean;
    // Set by operations shipped ahead of their backend so the error link stays quiet about the
    // expected "field not found in query_root" while the backing Hasura view isn't deployed yet.
    tolerateMissingSchema?: boolean;
  }
}

const authLink = setContext(async (_, { headers, token }) => {
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

const errorLink = onError((errorResponse) => {
  if (import.meta.env.MODE === "production") return;

  // Some operations are deliberately shipped ahead of their backend — a Hasura view that isn't
  // deployed in every environment yet (e.g. `agentUsageRequest`, the backend half of RTLCA-5010).
  // Such an operation opts in via `context.tolerateMissingSchema` so the expected
  // "field ... not found in type: 'query_root'" noise doesn't spam the console on every page view.
  // Any other GraphQL error, or a network error, on that operation still logs normally.
  const { operation, graphQLErrors, networkError } = errorResponse;
  const missingSchemaOnly =
    !!operation.getContext().tolerateMissingSchema &&
    !networkError &&
    !!graphQLErrors?.length &&
    graphQLErrors.every((graphQLError) => graphQLError.message.includes("not found in type: 'query_root'"));
  if (missingSchemaOnly) return;

  logErrorMessages(errorResponse);
});

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_HASURA_GRAPHQL_ENDPOINT,
});

// Retry transient network errors (e.g. brief Hasura cold starts or connectivity blips at login).
// Only retries on network errors, not GraphQL errors. Exponential backoff: 300ms, 600ms, 1200ms.
const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: (error) => !!error && !error.result,
  },
  delay: {
    initial: 300,
    max: 2000,
    jitter: true,
  },
});

const wsEndpoint = import.meta.env.VITE_HASURA_GRAPHQL_ENDPOINT.replace(/^http/, "ws");

export const wsClient = createWsClient({
  url: wsEndpoint,
  lazy: true, // connect only when needed
  retryAttempts: Infinity,
  keepAlive: 15000, // 15s
  shouldRetry: () => true,
  connectionParams: async () => {
    const token = gaiupApolloClient.defaultContext.token;
    const hasAccessAllProjects = gaiupApolloClient.defaultContext.hasAccessAllProjects;

    return {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(hasAccessAllProjects ? { "x-hasura-role": "accessAllProjects" } : {}),
      },
    };
  },
});

const wsLink = new GraphQLWsLink(wsClient);

// Split: subscriptions -> WS, others -> HTTP
const link = split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return def.kind === "OperationDefinition" && def.operation === "subscription";
  },
  from([errorLink, wsLink]),
  from([authLink, errorLink, retryLink, httpLink])
);

const cache = new InMemoryCache({
  typePolicies: {
    translationCategory: { keyFields: ["id", "languageCode"] },
  },
});

const gaiupApolloClient = new ApolloClient({
  cache,
  link,
  defaultOptions: {
    watchQuery: { fetchPolicy: "cache-and-network" },
    query: { fetchPolicy: "network-only" },
  },
});

export default gaiupApolloClient;
